// filepath: apps/web/src/editors/form-builder/FormCanvas.tsx
//
// FR-UI-BLD-1 AC-1 — center canvas. Renders the field list as a
// sortable list (HTML5 drag-and-drop). Each field row carries the
// data-field-id attribute so the InlineFieldError component can
// locate the error chip on save (HTTP 422).

import React from "react";
import { useTranslation } from "react-i18next";
import type { FieldDefinition, ValidationErrorWire } from "./types";
import { InlineFieldError } from "./InlineFieldError";

interface FormCanvasProps {
  fields: Record<string, FieldDefinition>;
  onSelectField: (fieldId: string) => void;
  onRemoveField: (fieldId: string) => void;
  selectedFieldId?: string;
  errors: ValidationErrorWire[];
  onDropField: (fieldType: string) => void;
}

export function FormCanvas({
  fields,
  onSelectField,
  onRemoveField,
  selectedFieldId,
  errors,
  onDropField,
}: FormCanvasProps): React.ReactElement {
  const { t } = useTranslation();
  const entries = Object.entries(fields);
  return (
    <section
      className="form-canvas"
      data-testid="form-canvas"
      onDragOver={(e) => {
        e.preventDefault();
      }}
      onDrop={(e) => {
        e.preventDefault();
        const ft = e.dataTransfer.getData("fieldType");
        if (ft) {
          onDropField(ft);
        }
      }}
    >
      <h2 className="form-canvas__title">{t("admin.builder.canvas.title")}</h2>
      {entries.length === 0 ? (
        <p className="form-canvas__empty">{t("admin.builder.canvas.empty")}</p>
      ) : (
        <ul className="form-canvas__list">
          {entries.map(([fieldId, field]) => (
            <li
              key={fieldId}
              className={`form-canvas__item ${
                selectedFieldId === fieldId ? "is-selected" : ""
              }`}
              data-field-id={fieldId}
              data-testid={`canvas-item-${fieldId}`}
              onClick={() => onSelectField(fieldId)}
            >
              <div className="form-canvas__item-label">
                <strong>{fieldId}</strong>
                <span className="form-canvas__item-type">
                  {field.field_type}
                </span>
              </div>
              <button
                type="button"
                className="form-canvas__item-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveField(fieldId);
                }}
                aria-label={t("admin.builder.canvas.remove", { id: fieldId })}
              >
                ✕
              </button>
              <InlineFieldError fieldId={fieldId} errors={errors} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
